"use client";
import { useMemo, useState } from "react";
import { PopularBrandItem, Range, TranslationData } from "@/common/lib/types";
import Button from "@/components/ui/button/Button";
import Icon from "@/components/ui/icon/Icon";
import { FilteredFacet } from "@/apiUtils/algoliaUtils/types";
import { extractSelectedSearchParams } from "@/utils/filter.utils";
import { closeIcon } from "@/constants/icons";
import FilterTabs from "./FilterTabs";
import TabContents from "./TabContents";
import SelectedFilters from "../selectedFilters";

export interface IProps {
  locale: string;
  lang: string;
  open: boolean;
  filters: FilteredFacet[];
  searchParams: Record<string, string>;
  totalProductCount: number;
  priceRange?: Range;
  translations: TranslationData;
  popularBrands: { title: string; brands: PopularBrandItem[] };
  onClose: () => void;
  onChange: (key: string, value: string) => void;
  onRemove: (key: string, value: string, all?: boolean) => void;
}

const AllFilters = ({
  locale,
  open,
  filters,
  lang,
  searchParams,
  totalProductCount,
  priceRange,
  translations,
  popularBrands,
  onClose,
  onChange,
  onRemove,
}: IProps) => {
  const [selectedTab, setSelectedTab] = useState(filters[0]?.type);
  const filter = useMemo(() => filters?.find((f) => f?.type === selectedTab), [selectedTab,filters]);

  const appliedFilters = useMemo(
    () => extractSelectedSearchParams(searchParams, locale, filters),
    [searchParams, locale, filters],
  );
  const hasAppliedParams = useMemo(() => Object?.keys(appliedFilters).length > 0, [appliedFilters]);

  const handleResetAll = () => {
    onRemove("", "", true);
  };

  const handleSelectTab = (tab: string) => {
    setSelectedTab(tab);
  };

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed left-0 top-0 z-20 h-[100dvh] w-full bg-black/60 ${open ? "block" : "hidden"}`}
      ></div>

      <div
        className={`fixed left-0 top-0 z-20 flex h-full w-full max-w-[30.5rem] flex-col bg-white transition-transform ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="relative flex h-[73px] justify-center py-6 shadow-[0_4px_4px_0_#EAEAEA80]">
          <p className="text-base font-bold">{translations?.plp?.plp_all_filters_label || ""}</p>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-8 top-8 -translate-y-1/2"
            title="close menu"
          >
            <Icon src={closeIcon} title="close" size={14} />
          </button>
        </div>

        <SelectedFilters
          filters={filters}
          lang={lang}
          translations={translations}
          appliedFilters={appliedFilters}
          onRemove={onRemove}
        />

        <div className="flex flex-1 overflow-y-auto">
          <div className="grid flex-1 grid-cols-7">
            <div className="col-span-2 max-h-max overflow-y-auto">
              <FilterTabs
                lang={lang}
                tabs={filters}
                selectedTab={selectedTab}
                searchParams={searchParams}
                onSelect={handleSelectTab}
              />
            </div>
            <div className="col-span-5 max-h-max overflow-y-auto p-2">
              <TabContents
                selectedTab={selectedTab}
                filter={filter}
                priceRange={priceRange}
                searchParams={searchParams}
                translations={translations}
                popularBrands={popularBrands}
                lang={lang}
                onChange={onChange}
              />
            </div>
          </div>
        </div>
        <div className="relative flex h-[73px] items-center justify-center gap-4 shadow-[0_4px_4px_0_#EAEAEA80]">
          <Button variant="outlined" disabled={!hasAppliedParams} onClick={handleResetAll} className="min-w-[157px]">
            {translations?.plp?.plp_reset_all_filters_label || ""}
          </Button>
          <Button disabled={totalProductCount === 0} onClick={onClose} className="min-w-[157px]">
            {totalProductCount === 0
              ? translations?.plp?.plp_filter_product_no_items_label || ""
              : totalProductCount > 1
                ? translations?.plp?.plp_filter_product_count_multiple_label?.replace(
                    "{{count}}",
                    `${totalProductCount}`,
                  )
                : translations?.plp?.plp_filter_product_count_single_label?.replace(
                    "{{count}}",
                    `${totalProductCount}`,
                  )}
          </Button>
        </div>
      </div>
    </>
  );
};

export default AllFilters;
